import { AuthUIProviderTanstack } from "@daveyplate/better-auth-ui/tanstack";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import type { MouseEvent, ReactNode } from "react";
import { useCallback } from "react";

import { ThemeProvider } from "@/components/theme-provider";
import { Toaster } from "@/components/ui/sonner";
import { authClient } from "@/lib/auth-client";

type AppProvidersProps = {
	queryClient: QueryClient;
	children: ReactNode;
};

type AuthUiLinkProps = {
	href: string;
	className?: string;
	children: ReactNode;
};

function isModifiedClick(event: MouseEvent<HTMLAnchorElement>) {
	return event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;
}

function isExternalHref(href: string) {
	return /^(https?:|mailto:|tel:)/.test(href);
}

function AuthUiTanStackLink({ href, className, children }: AuthUiLinkProps) {
	const navigate = useNavigate();

	const handleClick = useCallback(
		(event: MouseEvent<HTMLAnchorElement>) => {
			if (
				event.defaultPrevented ||
				event.button !== 0 ||
				isModifiedClick(event)
			) {
				return;
			}

			if (isExternalHref(href)) {
				return;
			}

			event.preventDefault();
			void navigate({ href });
		},
		[href, navigate],
	);

	return (
		<a className={className} href={href} onClick={handleClick}>
			{children}
		</a>
	);
}

export function AppProviders({ queryClient, children }: AppProvidersProps) {
	const navigate = useNavigate();

	const navigateToHref = useCallback(
		(href: string, replace = false) => {
			if (isExternalHref(href)) {
				return;
			}

			void navigate({ href, replace });
		},
		[navigate],
	);

	const authNavigate = useCallback(
		(href: string) => {
			navigateToHref(href, false);
		},
		[navigateToHref],
	);

	const authReplace = useCallback(
		(href: string) => {
			navigateToHref(href, true);
		},
		[navigateToHref],
	);

	return (
		<QueryClientProvider client={queryClient}>
			<ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
				<AuthUIProviderTanstack
					Link={AuthUiTanStackLink}
					account={{
						basePath: "/account",
						fields: ["image", "name"],
					}}
					authClient={authClient}
					basePath="/"
					credentials={false}
					emailOTP
					organization={{
						basePath: "/organization",
					}}
					navigate={authNavigate}
					replace={authReplace}
					redirectTo="/"
					signUp={false}
				>
					{children}
				</AuthUIProviderTanstack>
				<Toaster />
			</ThemeProvider>
		</QueryClientProvider>
	);
}
